import type { Breakdown, Match, MultiplierConfig, Player } from "@/types";
import type { ActivityId, PlayerId } from "@/types/branded";

export type MultiplierFn = (props: {
  deltaMu: number;
  sigma: number;
  expectedWin: number;
  player: Player;
}) => number;

export const SCALE = 50;

export const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export const applyScale =
  (scale: number): MultiplierFn =>
  () =>
    scale;

export const applyUpset: MultiplierFn = ({ expectedWin }) =>
  expectedWin < 0.4 ? 1 + Math.min((0.5 - expectedWin) * 2, 0.4) : 1;

export const applyStreak: MultiplierFn = ({ deltaMu, player }) =>
  deltaMu > 0 ? 1 + Math.min(player.stats.winStreak * 0.08, 0.4) : 1;

export const applyWeight =
  (weight: number): MultiplierFn =>
  () =>
    weight;

/**
 * Fatigue: diminishing returns as a player plays more games in a session.
 * Reduces points slightly after many games to prevent grinding dominance.
 */
export const applyFatigue =
  (halfLife: number = 20): MultiplierFn =>
  ({ player }) => {
    const games = player.stats.gamesPlayed;
    // Exponential decay: after `halfLife` games, multiplier = 0.5
    return Math.pow(0.5, games / halfLife);
  };

/**
 * Momentum: rewards recent performance trends.
 * Players on hot streaks (multiple recent wins) get a small bonus,
 * but unlike the streak multiplier, this scales more smoothly.
 */
export const applyMomentum =
  (factor: number = 0.05, cap: number = 0.3): MultiplierFn =>
  ({ deltaMu, player }) => {
    if (deltaMu <= 0) return 1;
    // Uses winStreak as a proxy for momentum
    const momentum = Math.min(player.stats.winStreak * factor, cap);
    return 1 + momentum;
  };

/**
 * Comeback: rewards players who win despite being significantly behind.
 * Triggers when a player's current rating is below the default starting mu
 * and they still manage to win, giving them a recovery boost.
 */
export const applyComeback =
  (threshold: number = 20, maxBonus: number = 0.5): MultiplierFn =>
  ({ deltaMu, player }) => {
    if (deltaMu <= 0) return 1;
    const deficit = 25 - player.rating.mu; // 25 = default starting mu
    if (deficit > threshold) {
      return 1 + Math.min((deficit - threshold) * 0.02, maxBonus);
    }
    return 1;
  };

/**
 * Consistency: rewards players whose sigma (uncertainty) is low,
 * meaning their skill level is well-established.
 * Stable players get a small bonus to accelerate point accumulation.
 */
export const applyConsistency =
  (sigmaThreshold: number = 6, bonus: number = 0.15): MultiplierFn =>
  ({ player }) => {
    if (player.rating.sigma < sigmaThreshold) {
      const factor = 1 - player.rating.sigma / sigmaThreshold;
      return 1 + factor * bonus;
    }
    return 1;
  };

/**
 * Closeness: bonus for matches where the result is very tight,
 * measured by the absolute deltaMu being small. Close games
 * are rewarded because they indicate competitive matchups.
 */
export const applyCloseness =
  (maxDelta: number = 0.5, bonus: number = 0.2): MultiplierFn =>
  ({ deltaMu }) => {
    const absDelta = Math.abs(deltaMu);
    if (absDelta < maxDelta) {
      return 1 + (1 - absDelta / maxDelta) * bonus;
    }
    return 1;
  };

/**
 * Variety: rewards players for switching between different activities.
 * If a player hasn't played this activity recently, they get a bonus.
 * Encourages trying new things rather than grinding one game.
 */
export const applyVariety =
  (
    decay: number = 3,
    bonus: number = 0.2,
    activityId?: ActivityId,
    recentMatches?: Match[],
  ): MultiplierFn =>
  ({ player }) => {
    if (!activityId || !recentMatches || recentMatches.length === 0) return 1;
    // Count how many of the last `decay*2` matches were this same activity for this player
    const recent = recentMatches.slice(-decay * 2);
    const sameCount = recent.filter(
      (m) => m.activityId === activityId && m.teams.flat().includes(player.id),
    ).length;
    if (sameCount === 0) return 1 + bonus; // totally new activity → full bonus
    const factor = Math.max(0, 1 - sameCount / decay);
    return 1 + factor * bonus;
  };

/**
 * Rivalry: bonus for beating a player who beat you recently.
 * Checks recent match results for opponents who won against you,
 * and gives a bonus when you beat them back. Creates narratives!
 */
export const applyRivalry =
  (
    lookback: number = 10,
    bonus: number = 0.25,
    currentMatch?: Match,
    recentMatches?: Match[],
    playersById?: Record<PlayerId, Player>,
  ): MultiplierFn =>
  ({ deltaMu, player }) => {
    if (deltaMu <= 0 || !currentMatch || !recentMatches || !playersById) return 1;
    // Find opponents in the current match who are on a different team
    const playerTeamIdx = currentMatch.teams.findIndex((t) => t.includes(player.id));
    if (playerTeamIdx < 0) return 1;
    const opponents = currentMatch.teams.filter((_, i) => i !== playerTeamIdx).flat();
    if (opponents.length === 0) return 1;

    // Check recent matches for losses against any of these opponents
    const recent = recentMatches.slice(-lookback);
    let hasRivalry = false;
    for (const m of recent) {
      const myTeamIdx = m.teams.findIndex((t) => t.includes(player.id));
      if (myTeamIdx < 0) continue;
      const myRank = m.ranks[myTeamIdx];
      // Did I lose? And was one of my current opponents on a winning team?
      for (let ti = 0; ti < m.teams.length; ti++) {
        if (ti === myTeamIdx) continue;
        const theirRank = m.ranks[ti];
        if (theirRank !== undefined && myRank !== undefined && theirRank < myRank) {
          // This team beat me — check if any current opponent was on it
          if (m.teams[ti]!.some((pid) => opponents.includes(pid))) {
            hasRivalry = true;
            break;
          }
        }
      }
      if (hasRivalry) break;
    }
    return hasRivalry ? 1 + bonus : 1;
  };

/**
 * Build multipliers from the admin config stored in state.
 * Context params (activityId, recentMatches, currentMatch, playersById) are
 * needed for the variety and rivalry multipliers.
 */
export const buildMultipliers = (
  config: MultiplierConfig,
  activityWeight: number,
  context?: {
    activityId?: ActivityId;
    recentMatches?: Match[];
    currentMatch?: Match;
    playersById?: Record<PlayerId, Player>;
  },
): Record<string, MultiplierFn> => {
  const m: Record<string, MultiplierFn> = {};

  if (config.scale.enabled) m.scale = applyScale(config.scale.factor);

  if (config.weight.enabled) m.weight = applyWeight(activityWeight);

  if (config.upset.enabled) m.upset = applyUpset;

  if (config.streak.enabled) m.streak = applyStreak;

  if (config.fatigue.enabled) m.fatigue = applyFatigue(config.fatigue.halfLife);

  if (config.momentum.enabled)
    m.momentum = applyMomentum(config.momentum.factor, config.momentum.cap);

  if (config.comeback.enabled)
    m.comeback = applyComeback(config.comeback.threshold, config.comeback.maxBonus);

  if (config.consistency.enabled)
    m.consistency = applyConsistency(config.consistency.sigmaThreshold, config.consistency.bonus);

  if (config.closeness.enabled)
    m.closeness = applyCloseness(config.closeness.maxDelta, config.closeness.bonus);

  if (config.variety.enabled)
    m.variety = applyVariety(
      config.variety.decay,
      config.variety.bonus,
      context?.activityId,
      context?.recentMatches,
    );

  if (config.rivalry.enabled)
    m.rivalry = applyRivalry(
      config.rivalry.lookback,
      config.rivalry.bonus,
      context?.currentMatch,
      context?.recentMatches,
      context?.playersById,
    );

  // If nothing is enabled, at least include weight so points work
  if (Object.keys(m).length === 0) m.weight = applyWeight(activityWeight);
  return m;
};

type Props = {
  deltaMu: number;
  sigma: number;
  expectedWin: number;
  player: Player;
  multipliers: Record<string, MultiplierFn>;
};

export const calculatePoints = ({
  deltaMu,
  sigma,
  expectedWin,
  player,
  multipliers,
}: Props): { points: number; breakdown: Breakdown } => {
  const base = deltaMu / sigma;
  const multiplierValues: Record<string, number> = {};
  let bonusSum = 0;
  for (const [key, fn] of Object.entries(multipliers)) {
    const value = fn({ deltaMu, sigma, expectedWin, player });
    multiplierValues[key] = value;
    bonusSum += value - 1;
  }
  const totalMultiplier = 1 + bonusSum;
  const points = clamp(base * totalMultiplier, -100, 100);

  // Avoid division by zero when points is 0
  if (points === 0) {
    const breakdown: Breakdown = { base: 0 };
    for (const key of Object.keys(multiplierValues)) {
      breakdown[key] = 0;
    }
    return { points: 0, breakdown };
  }

  const breakdown: Breakdown = { base: base / points };
  for (const [key, value] of Object.entries(multiplierValues)) {
    const contribution = (base * (value - 1)) / points;
    breakdown[key] = contribution;
  }

  return { points, breakdown };
};
